<template>
  <div>
    <div class="box">
      <h2 class="title">{{$t('publicParking.regularization.navigation.title')}}</h2>
      <p class="has-text-centered">
        <button class="button is-rounded is-primary" @click="start()">
          {{$t('publicParking.regularization.button.title')}}
        </button>
      </p>
    </div>

    <zul-modal ref="conflictModal"
      :title="$t('publicParking.regularization.paymentPending.title')"
      :primary="$t('publicParking.regularization.paymentPending.button')"
      @primary="resolveConflict"
    >
      <fieldset>
        <p>{{$t('publicParking.regularization.paymentPending.info')}}</p>
      </fieldset>
    </zul-modal>

    <zul-modal ref="modal"
      :title="$t('publicParking.regularization.queryModal.title')"
    >
      <div v-if="vehicle == null">
        <div v-if="vehicles.length > 0">
          <Vehicle
            v-for="vehicle in vehicles"
            :key="vehicle.id"
            :data="vehicle"
            buttonType="arrow"
            @click="carSelected"
          ></Vehicle>
        </div>
        <div class="vehicles-empty" v-else>
          <p>
            {{$t('publicParking.regularization.vehicleEmpty')}}
          </p>
        </div>
      </div>
      <div v-else>
        <fieldset v-if="isLoading">
          <p class="loading">{{$t('global.loading')}}</p>
        </fieldset>
        <fieldset v-if="!isLoading && step == 1 && supportsActCode" class="form">
          <zul-input
            type="text"
            v-model="form.actCode"
            name="actCode"
            icon="document"
            v-validate="'required'"
            placeholder="A000000000"
            :label="$t('publicParking.regularization.actCode.field.title')"
            :data-vv-as="$t('publicParking.regularization.actCode.field.title')"
            data-vv-validate-on="custom"></zul-input>
          <span>{{ errors.firs$t('actCode') }}</span>
        </fieldset>
      </div>

    </zul-modal>

    <zul-modal ref="detailModal"
      :title="$t('publicParking.regularization.navigation.title')"
      :primary="$t('global.pay')"
      :secondary="$t('global.back')"
      @primary="payment"
      @secondary="start"
    >
      <fieldset v-if="isLoading">
        <p class="loading">{{$t('global.loading')}}</p>
      </fieldset>
      <div v-if="!isLoading && step == 2">
        <p class="data-control">
          <span class="label">{{$t('global.form.city.title')}}</span>
          {{regularization.city}}
        </p>
        <p class="data-control">
          <span class="label">{{$t('global.form.date.title')}}</span>
          {{regularization.date | moment("DD/MM/YYYY")}}
        </p>
        <p class="data-control">
          <span class="label">{{$t('global.form.hour.title')}}</span>
          {{regularization.date | moment("HH:mm")}}
        </p>
        <p class="data-control">
          <span class="label">{{$t('global.form.registrationPlate.title')}}</span>
          {{regularization.registrationPlate}}
        </p>
        <p class="data-control">
          <span class="label">{{$t('publicParking.regularization.actCode.field.title')}}</span>
          {{regularization.actCode}}
        </p>
        <p class="data-control">
          <span class="label">{{$t('global.dueDate')}}</span>
          {{regularization.dueDate | moment("DD/MM/YYYY HH:mm")}}
        </p>
        <p class="data-control">
          <span class="label">{{$t('global.form.value.title')}}</span>
          <span class="currency">R$ </span>{{regularization.amount | currency("", 2, { decimalSeparator: "," })}}
        </p>
      </div>
    </zul-modal>

    <zul-modal ref="paymentTypeModal"
      :title="$t('publicParking.regularization.payment.title')"
      :secondary="$t('global.back')"
      @secondary="search"
    >
      <fieldset v-if="isLoading">
        <p class="loading">{{$t('global.loading')}}</p>
      </fieldset>
      <fieldset v-if="!isLoading">
        <zul-button
          class="is-large"
          v-for="p in paymentTypes"
          :key="p.type"
          :data="{}"
          @click="setPaymentType(p.type)"
          :disabled="!p.enabled"
        >
          <span
            slot="icon"
            :class="{
              icon: true,
              'is-card': p.type == 'CREDIT' || p.type == 'DEBIT',
              'is-bankslip': p.type == 'BANKSLIP',
            }"
          >
            <svg
              v-if="p.type === 'PIX'"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.7317 13.44C11.9423 13.2295 12.3094 13.2288 12.5199 13.44L15.5198 16.4397C16.0746 16.9944 16.8126 17.2999 17.5973 17.2999H17.9587L14.1484 21.1101C12.9619 22.2966 11.0381 22.2966 9.85151 21.1101L6.05229 17.3108H6.64342C7.42813 17.3108 8.1661 17.0053 8.721 16.4507L11.7317 13.44ZM5.20156 7.53994C5.25 7.55819 5.30137 7.57118 5.35621 7.57118H6.64344C7.1847 7.57118 7.7144 7.79064 8.09688 8.17331L11.1079 11.1843C11.3886 11.4648 11.7572 11.6053 12.126 11.6053C12.4946 11.6053 12.8634 11.4648 13.144 11.1841L16.1438 8.18422C16.5262 7.80155 17.056 7.58209 17.5973 7.58209H18.6441C18.702 7.58209 18.7569 7.56939 18.8076 7.54907L21.1101 9.85158C22.2966 11.0381 22.2966 12.9619 21.1101 14.1485L18.8077 16.4509C18.7569 16.4305 18.7021 16.4179 18.6441 16.4179H17.5973C17.056 16.4179 16.5262 16.1985 16.1438 15.8157L13.144 12.8162C12.6004 12.272 11.6518 12.2722 11.1076 12.816L8.09688 15.8267C7.7144 16.2094 7.1847 16.4288 6.64344 16.4288H5.35621C5.30137 16.4288 5.24991 16.4418 5.20147 16.4601L2.88993 14.1485C1.70336 12.9619 1.70336 11.0381 2.88993 9.85158L5.20156 7.53994ZM9.85153 2.88993C11.0381 1.70336 12.9619 1.70336 14.1485 2.88993L17.9586 6.70008H17.5973C16.8126 6.70008 16.0746 7.00562 15.5197 7.56024L12.52 10.5603C12.3026 10.7783 11.9485 10.7774 11.7317 10.5606L8.72093 7.54933C8.16611 6.99471 7.42815 6.68908 6.64344 6.68908H6.05241L9.85153 2.88993Z"
                fill="currentColor"
              />
            </svg>
          </span>

          {{ p.name | lowercase | capitalize }}
        </zul-button>
      </fieldset>
    </zul-modal>

    <zul-modal ref="paymentMethodModal"
      :title="$t('global.payment')"
      :secondary="$t('global.back')"
      @secondary="payment"
    >
      <fieldset v-if="purchaseData.paymentType == 'credit'">
            <zul-button
              class="is-large"
              :data="{}"
              v-for="p in paymentMethods"
              :key="p.id"
              @click="setPaymentMethod(p)"
            >
              **** **** **** {{ p.lastDigits }}
              <span slot="sub"
                ><img :src="p.processor.image['@2x']" width="60"
              /></span>
            </zul-button>

            <a class="button is-text is-medium" @click="addUserCard('credit')"
              >{{$t('publicParking.regularization.payment.newCreditCardButton.title')}}</a
            >
      </fieldset>
      <fieldset v-if="purchaseData.paymentType == 'debit'">
          <zul-button
            class="is-large"
            :data="{}"
            v-for="p in paymentDebitMethods"
            :key="p.id"
            @click="setPaymentMethod(p)"
          >
            **** **** **** {{ p.lastDigits }}
            <span slot="sub"
              ><img :src="p.processor.image['@2x']" width="60"
            /></span>
          </zul-button>

          <a class="button is-text is-medium" @click="addUserCard('debit')"
            >{{$t('publicParking.regularization.payment.newDebitCardButton.title')}}</a
          >
      </fieldset>
    </zul-modal>

    <zul-modal ref="purchaseModal"
      :title="$t('global.confirmation')"
      :primary="$t('global.send')"
      :secondary="$t('global.back')"
      @primary="placeOrder"
      @secondary="payment"
    >
      <fieldset v-if="purchaseData.paymentType === 'credit' || purchaseData.paymentType === 'debit'">
        <p class="info">
          {{$t('publicParking.regularization.payment.creditOrDebit.askConfirm1')}}
          {{ purchaseData.paymentType === "credit" ? $t('global.credit') : $t('global.debit') }}
          {{$t('publicParking.regularization.payment.creditOrDebit.askConfirm2')}}
        </p>
        <div class="summary">
          <zul-button v-if="purchaseData.card" class="is-view is-large is-secondary">
            **** **** **** {{ purchaseData.card.lastDigits }}
            <span slot="sub"
              ><img
                :src="purchaseData.card.processor.image['@2x']"
                width="60"
            /></span>
          </zul-button>
        </div>
      </fieldset>
      <fieldset v-if="purchaseData.paymentType === 'bankslip'">
        <p class="info">
          {{$t('publicParking.regularization.payment.bankslip.askConfirm1')}}
        </p>
        <div class="summary">
          <zul-button class="is-view is-large is-secondary">
            <span slot="icon" class="icon is-bankslip"></span>
            {{$t('publicParking.regularization.payment.bankslip.title')}}
          </zul-button>

          <div class="warning">
            <h3 class="title">{{$t('publicParking.regularization.payment.bankslip.warning.title')}}</h3>
            <p class="warning-message">{{$t('publicParking.regularization.payment.bankslip.warning.message')}}</p>
          </div>
        </div>
      </fieldset>
      <fieldset v-if="purchaseData.paymentType === 'pix'">
        <p class="info">{{$t('publicParking.regularization.payment.pix.askConfirm1')}}</p>
        <div class="summary">
          <zul-button class="is-view is-large is-secondary">
            <span slot="icon" class="icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.7317 13.44C11.9423 13.2295 12.3094 13.2288 12.5199 13.44L15.5198 16.4397C16.0746 16.9944 16.8126 17.2999 17.5973 17.2999H17.9587L14.1484 21.1101C12.9619 22.2966 11.0381 22.2966 9.85151 21.1101L6.05229 17.3108H6.64342C7.42813 17.3108 8.1661 17.0053 8.721 16.4507L11.7317 13.44ZM5.20156 7.53994C5.25 7.55819 5.30137 7.57118 5.35621 7.57118H6.64344C7.1847 7.57118 7.7144 7.79064 8.09688 8.17331L11.1079 11.1843C11.3886 11.4648 11.7572 11.6053 12.126 11.6053C12.4946 11.6053 12.8634 11.4648 13.144 11.1841L16.1438 8.18422C16.5262 7.80155 17.056 7.58209 17.5973 7.58209H18.6441C18.702 7.58209 18.7569 7.56939 18.8076 7.54907L21.1101 9.85158C22.2966 11.0381 22.2966 12.9619 21.1101 14.1485L18.8077 16.4509C18.7569 16.4305 18.7021 16.4179 18.6441 16.4179H17.5973C17.056 16.4179 16.5262 16.1985 16.1438 15.8157L13.144 12.8162C12.6004 12.272 11.6518 12.2722 11.1076 12.816L8.09688 15.8267C7.7144 16.2094 7.1847 16.4288 6.64344 16.4288H5.35621C5.30137 16.4288 5.24991 16.4418 5.20147 16.4601L2.88993 14.1485C1.70336 12.9619 1.70336 11.0381 2.88993 9.85158L5.20156 7.53994ZM9.85153 2.88993C11.0381 1.70336 12.9619 1.70336 14.1485 2.88993L17.9586 6.70008H17.5973C16.8126 6.70008 16.0746 7.00562 15.5197 7.56024L12.52 10.5603C12.3026 10.7783 11.9485 10.7774 11.7317 10.5606L8.72093 7.54933C8.16611 6.99471 7.42815 6.68908 6.64344 6.68908H6.05241L9.85153 2.88993Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            Pix
          </zul-button>

          <div class="warning">
            <h3 class="title">{{$t('publicParking.regularization.payment.pix.warning.title')}}</h3>
            <p class="warning-message" v-html="$t('publicParking.regularization.payment.pix.warning.message')" />
          </div>
        </div>
      </fieldset>
    </zul-modal>

    <zul-modal ref="confirmModal"
      class="is-medium"
      type="neutral"
      :primary="$t('global.close')"
      @primary="close"
    >
      <fieldset v-if="isLoading">
        <p class="loading">{{$t('global.loading')}}</p>
      </fieldset>
      <fieldset
        v-else-if="purchaseResult && purchaseResult.status == 'PENDING' && purchaseResult.paymentType === 'BANKSLIP'"
      >
        <h2 class="title">{{$t('publicParking.regularization.payment.bankslip.warning.message')}}</h2>

        <p class="info">{{$t('publicParking.regularization.payment.success.info')}}</p>
        <p class="info">
          {{$t('global.dueDate')}}: <strong>{{ purchaseResult.dueDate }}</strong>
        </p>

        <div class="summary">
          <bankslip :data="purchaseResult"></bankslip>
        </div>
      </fieldset>

      <fieldset v-else>
        <div v-if="purchaseResult.status === 'FINISHED'">
          <span class="icon is-success-order"></span>
          <h2 class="title">{{$t('publicParking.regularization.payment.success.title')}}</h2>
          <p>{{$t('publicParking.regularization.payment.success.info')}}</p>
        </div>
        <div v-else>
          <h2 class="title">{{$t('publicParking.regularization.payment.processing.title')}}</h2>
          <p class="loading">{{$t('publicParking.regularization.payment.processing.info')}}</p>
        </div>
      </fieldset>
    </zul-modal>

    <PixModal ref="pixConfirmModal"
      :order="purchaseResult"
    ></PixModal>
  </div>
</template>

<script>
import ZulButton from "../../components/Button.vue";
import PixModal from "../../components/Pix.vue";
import Bankslip from "../../components/Bankslip.vue";
import Vehicle from "../../components/Vehicle.vue";
import QrcodeVue from "qrcode.vue";

export default {
  props: {
    supportsActCode: Boolean
  },
  data () {
    const user = this.$auth.user()
    const vehicles = this.$auth.user().vehicles;

    return {
      step: 0,
      isLoading: false,
      user: user,
      paymentMethods: [],
      paymentDebitMethods: [],
      form: {
        actCode: ""
      },
      paymentTypes: [],
      purchaseData: {},
      regularization: {},
      purchaseResult: {},
      message: null,
      polling: null,
      conflict: null,
      vehicles: vehicles || [],
      vehicle: null,
    }
  },
  created() {
    this.loadUserPaymentMethods();

    this.$root.$on("cancel-bankslip-end", () => {
      this.isLoading = false;

      if (this.$refs.confirmModal) {
        this.$refs.confirmModal.close();
      }
      if (this.$refs.conflictModal) {
        this.$refs.conflictModal.close();
      }
    });

    this.$root.$on("cancel-pix-end", () => {
      this.isLoading = false;

      if (this.$refs.confirmModal) {
        this.$refs.confirmModal.close();
      }
      if (this.$refs.conflictModal) {
        this.$refs.conflictModal.close();
      }
    });

    this.$root.$on("pix-approved", (newPurchaseState) => {
      this.purchaseResult = newPurchaseState;

      this.$refs.conflictModal.close()
      this.$refs.pixConfirmModal.close()
      this.$refs.purchaseModal.close()

      this.$refs.confirmModal.open();

      this.$root.$emit("reload-historic");
    })
  },
  beforeDestroy () {
    clearInterval(this.polling)
  },
  methods: {
    // step 0
    async carSelected (data) {
      this.vehicle = data.registrationPlate;

      if (!this.supportsActCode) {
        this.search()
      }
    },
    // step 1
    start () {
      this.reset();

      this.step = 1;
      this.$refs.detailModal.close();
      this.$refs.modal.open();
    },
    // step 2
    search() {
      this.step = 1
      this.$refs.modal.open();
      if (this.isLoading) return
      this.isLoading = true

      this.$refs.modal.startLoading();

      this.$validator.validate().then(valid => {
        if (!valid) {
          throw new Error(this.$t('global.validation.message'));
        }
        return valid
      }).then(() => {
        const url = this.form.actCode ?
          `/regularization?actCode=${this.form.actCode}&registrationPlate=${this.vehicle}`
          :
          `/regularization?registrationPlate=${this.vehicle.replace(/-/g,'')}`
        this.REGULARIZATION
          .get(url)
          .then((res) => {
              this.isLoading = false
              this.$refs.modal.stopLoading();
              if (res.status === 202) {
                const message = this.t("publicParking.regularization.payed")
                this.$buefy.toast.open({
                  message,
                  type: "is-success",
                });
              } if (res.status === 200) {
                this.regularization = res.data

                if(this.regularization?.status === 'REGULARIZED' || this.regularization?.order?.status === 'PAYMENT_APPROVED') {
                  const message = this.t("publicParking.regularization.payed")
                  this.$buefy.toast.open({
                    message,
                    type: "is-success",
                  });
                } else {
                  this.$refs.modal.close()
                  this.$refs.paymentTypeModal.close()
                  this.$refs.detailModal.open()
                  this.step = 2
                }
              }
          })
          .catch((err)=> {
            this.isLoading = false
            this.$refs.modal.stopLoading();

            if (err?.response?.status == 402) {
              this.purchaseResult = err?.response?.data?.order;
              this.conflict = err?.response?.data?.order
              this.$refs.conflictModal.open()
              this.$refs.modal.close()
            } else if(err?.response?.status === 404) {
              const message =
              (((err || {}).response || {}).data || {}).message || 'Não existe regularização para esse veículo.'

              this.$buefy.toast.open({
                message,
                type: "is-success",
              });

              this.start();
            } else {
              const message =
              (((err || {}).response || {}).data || {}).message || this.t("global.error.message")
              this.$buefy.toast.open({
                message,
                type: "is-danger",
              });

              this.start();
            }
          })
      })
      .catch((error) => {
        this.isLoading = false
        this.$refs.modal.stopLoading();
        const message = error.message
        this.$buefy.toast.open({
          message,
          type: "is-danger",
        });
      })
    },
    // step 2.1
    resolveConflict() {
      this.purchaseResult = {
        product: {
          total: this.conflict.total
        },
        ...this.conflict
      }
      this.isLoading = false
      if (this.purchaseResult.paymentType.toUpperCase() === "BANKSLIP") {
        this.$refs.confirmModal.open();
        this.$refs.conflictModal.close();
        this.conflict = null
      }
      if (this.purchaseResult.paymentType.toUpperCase() === "PIX") {
        this.$refs.pixConfirmModal.open(false);
        this.$refs.purchaseModal.close();
        this.$refs.confirmModal.close();
        this.conflict = null;
      }
    },
    // step 3
    payment() {
      if (this.isLoading) return
      this.$refs.detailModal.open();
      this.$refs.purchaseModal.close();
      this.$refs.paymentMethodModal.close();

      this.isLoading = true
      this.$refs.detailModal.startLoading();

      this.paymentTypes = [];
      this.$http
        .get(`/order/paymenttypes?action=REGULARIZATION&amount=${this.regularization.amount}`)
        .then((res) => {
          this.paymentTypes = res.data.items.filter(
            (o) =>
              o.type === "BANKSLIP" ||
              o.type === "PIX" ||
              o.type === "DEBIT" ||
              (o.type === "CREDIT" && !o.wallet)
          );

          this.isLoading = false
          this.$refs.detailModal.stopLoading();
          this.$refs.detailModal.close()
          this.$refs.paymentTypeModal.open()
        })
        .catch((err)=>{
          this.isLoading = false
          this.$refs.detailModal.stopLoading();
          const message =
            (((err || {}).response || {}).data || {}).message || this.t("global.error.message");
          this.$buefy.toast.open({
            message,
            type: "is-danger",
          });
        })
    },
    // step 4
    setPaymentType(type) {
      this.isLoading = false;

      if (type == undefined) {
        type = this.purchaseData.paymentType
      } else {
        type = type.toLowerCase();
        this.purchaseData = {...this.purchaseData, paymentType: type}
      }

      this.$refs.paymentTypeModal.close();

      if (type === "credit" || type === "debit") {
        this.loadUserPaymentMethods()
        this.$refs.paymentMethodModal.open()
      } else if (type === "bankslip") {
        this.$refs.purchaseModal.open()
        this.$refs.purchaseModal.setProps({ labels: { primary: this.t("global.confirm") } });
      } else if (type === "pix") {
        this.$refs.purchaseModal.open()
        this.$refs.purchaseModal.setProps({ labels: { primary: this.$t('publicParking.regularization.payment.pix.generateButton.title')  } });
      }
    },
    // step 4.1
    setPaymentMethod(card) {
      this.purchaseData = {...this.purchaseData, card: card}

      this.isLoading = true;

      this.$http
        .get(
          `/user/payment/${this.purchaseData.card.id}`
        )
        .then(() => {
          this.$refs.purchaseModal.setProps({
            labels: { primary: this.$t('global.confirm') },
          });
          this.isLoading = false;
        });
      this.$refs.paymentMethodModal.close()
      this.$refs.purchaseModal.open()
    },
    reset() {
      this.step = 0;
      this.isLoading = false;
      this.paymentMethods = [];
      this.paymentDebitMethods = [];
      this.form = {
        actCode: ""
      },
      this.paymentTypes = [];
      this.purchaseData = {};
      this.regularization = {};
      this.purchaseResult = {};
      this.message = null;
      this.polling = null;
      this.conflict = null;
      this.vehicle = null;

      this.$refs.purchaseModal.stopLoading();
    },
    loadUserPaymentMethods() {
      this.paymentMethods = this.$auth
        .user()
        .paymentMethods.filter((p) => p.type === "CREDIT");
      this.paymentDebitMethods = this.$auth
        .user()
        .paymentMethods.filter((p) => p.type === "DEBIT");
    },
    addUserCard(type) {
      this.$root.$emit("add-user-card", type);
      this.$root.$on("add-user-card-end", () => {
        this.loadUserPaymentMethods();
      });
    },
    placeOrder () {
      let data = {
        actCode: this.regularization.actCode || null,
        registrationPlate: this.regularization.registrationPlate,
        total: this.regularization.amount,
        paymentType: this.purchaseData.paymentType?.toUpperCase(),
      }

      if (
        this.purchaseData.paymentType === "credit" ||
        this.purchaseData.paymentType === "debit"
      ) {
        data.paymentMethodId = this.purchaseData.card.id;
      } else if (this.purchaseData.paymentType === "bankslip") {
        // data.append("email", this.$auth.user().email);
      } else {
        const redeemCode =
          (
            (
              this.paymentTypes.find(
                (payment) => payment.type.toLowerCase() === "pix"
              ) || {}
            ).redeem || {}
          ).code || null;

        if (redeemCode) {
          data.redeemCode = redeemCode;
        }
      }

      this.$refs.purchaseModal.startLoading();
      this.message = null;

      this.REGULARIZATION.post(`regularization/order`, data).then(
        (res) => {
          if (res.status === 200) {
            this.purchaseResult = res.data;

            this.$refs.confirmModal.open();

            this.polling = setInterval(() => {
              this.REGULARIZATION.get(`regularization/order/${this.purchaseResult.id}`).then(
                (res) => {
                  this.purchaseResult = {
                    product: {
                      total: res.data.total
                    },
                    ...res.data
                  }

                  if (this.purchaseResult.status === "PAYMENT_REJECTED") {
                    clearInterval(this.polling)
                    throw new Error(this.purchaseResult.message)
                  }

                  if (this.purchaseResult?.paymentType?.toLowerCase() === "pix") {
                    if(this.purchaseResult.status === "PENDING") {
                      this.$refs.pixConfirmModal.open(false);
                      this.$refs.purchaseModal.close();
                      this.$refs.confirmModal.close();
                      clearInterval(this.polling);
                    }
                  } else if (this.purchaseResult?.paymentType?.toLowerCase() === "bankslip") {
                    if (this.purchaseResult.status === "PENDING") {
                      this.$root.$emit("set-pending-order", this.purchaseResult);
                      clearInterval(this.polling);
                      this.$refs.purchaseModal.close();
                    }
                  } else {
                    if (this.purchaseResult?.status === "FINISHED") {
                      this.$root.$emit("reload-historic");
                      this.$refs.purchaseModal.close();
                      this.$refs.confirmModal.open();
                      clearInterval(this.polling);
                    }
                  }
                })
                .catch((err) => {
                  clearInterval(this.polling);

                  const message =
                    err?.response?.data?.message || err?.message || err || this.$t('global.error.message');

                  this.$refs.purchaseModal.stopLoading();
                  this.$refs.confirmModal.close();

                  this.$buefy.toast.open({
                    message,
                    type: "is-danger",
                  });
                });
            }, 2000)
          }
        },
        (err) => {
          const message =
            err?.response?.data?.message || err?.message || err || this.$t('global.error.message');

          this.$refs.purchaseModal.stopLoading();

          clearInterval(this.polling);

          this.$buefy.toast.open({
            message,
            type: "is-danger",
          });
        }
      );
    },
    close() {
      this.$refs.confirmModal.close();
    }
  },
  components: {
    Bankslip,
    ZulButton,
    QrcodeVue,
    PixModal,
    Vehicle,
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  @import '../../assets/sass/variables';

  .label {
    color: #666 !important;
    font-size: 13px;
  }

  .data-control {
    margin-bottom: 10px;
  }

  fieldset {
    border: none;
    text-align: center;

    .info {
      font-size: $size-6;
      margin: 0.75rem 2rem 1.5rem;
    }
    .warning {
      padding: 0.75rem 1.5rem 0.5rem;
      font-size: $size-7;

      .title {
        font-size: $size-6;
        text-transform: uppercase;
      }
    }

    .is-success-order {
      display: inline-block;
      margin-bottom: 2rem;
    }

    .is-large & {
      .warning {
        padding: 0.75rem 3rem 0.5rem;
      }
    }
  }



</style>

<style lang="scss">
  .warning {
    strong {
      color: #fff !important;
      font-weight: 700;
    }
  }
</style>
